import uniqid from 'uniqid';
import { Cafe, Dish, Order, OrderItem, OrderItemStatus, OrderLog, Worker } from '../../apiTypes';
import { createBatch } from '../../utils/api';
import { canClose } from '../../utils/canCloseOrder';

type Props = {
  cafeSlug: string;
  cafe: DbDocument<Cafe>;
  order: DbDocument<Order>;
  worker: DbDocument<Worker>;
  item: OrderItem;
  dish: DbDocument<Dish>;
};

export const nextOrderItemStatus = async ({ order, worker, item, cafeSlug, dish, cafe }: Props) => {
  if (!worker.credentials.change_order_statuses) {
    return;
  }

  if (worker) {
    const isShortSatuses = cafe.orderStatusesSetting === 'short';
    const newStatusMap: Record<OrderItemStatus, OrderItemStatus> = {
      ordered: isShortSatuses ? 'complete' : 'processing',
      processing: isShortSatuses ? 'complete' : 'ready',
      ready: isShortSatuses ? 'complete' : 'complete',
      complete: 'complete',
    };

    const newItems = { ...order.items, [item.id]: { ...item, status: newStatusMap[item.status] } };

    const batch = createBatch();

    batch.update(`cafes/${cafeSlug}/orders/${order?.id}`, {
      [`items.${item.id}.status`]: newStatusMap[item.status],
      [`items.${item.id}.startProcessingTime`]: new Date().getTime(),
    });

    const updatedOrderLog: OrderLog = {
      time: Date.now(),
      workerId: worker.id,
      workerName: worker.name,
      type: 'changeItem',
      dishName: dish.name,
      state: {
        workerId: order.workerId,
        workerName: order.workerName,
        tableId: order.tableId,
        tableName: order.tableName,
        clientId: order.clientId || '',
        clientName: order.clientName || '',
        discount: order.discount || 0,
        items: newItems,
        paid: null,
        status: order.status || null,
        delivery: order.delivery || null,
      },
    };

    batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order?.id}/logs/${uniqid()}`, updatedOrderLog);

    if (newStatusMap[item.status] === 'complete' && !!order.paid) {
      batch.update<Order>(`cafes/${cafeSlug}/orders/${order?.id}`, {
        closed: canClose({ ...order, items: newItems }),
      });
    }

    await batch.commit();
  }
};
